import { useCallback, useEffect, useMemo, useState } from "react"
import { AppUIContext } from "./AppUIContext"
import config from "config"
import { useNavigationHistory } from "./hooks"
import { useAppNavigation } from "./hooks/useAppNavigation/useAppNavigation"
import { useVenueController } from "hooks/venue/useVenueController"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import {
  removeBodyWheelchairClass,
  addBodyWheelchairClass,
} from "utils/wheelchair"
import { useWheelchairModeConfig } from "hooks/accessibility"
import { useTranslation } from "providers/i18n"
import { getDirectionFromLanguage } from "utils/theme"
import { useSearchParams } from "react-router-dom"
import { Q_QUERY, Q_SEARCH, Q_MODE } from "constant"

export const AppUIProvider = ({ children }) => {
  const { i18n } = useTranslation()
  const { initVenueController } = useVenueController()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sidebarState, setSidebarState] = useState(false)
  const [mapLayout, setMapLayout] = useState("full")
  const [isIdleTimerPaused, setIdleTimerPaused] = useState(false)

  const openSearchState = useMemo(() => {
    return searchParams.get(Q_SEARCH) === "1"
  }, [searchParams])

  const setOpenSearchState = useCallback(
    (open) => {
      setSearchParams(
        (prev) => {
          if (open) {
            prev.set(Q_SEARCH, "1")
          } else {
            prev.delete(Q_SEARCH)
            prev.delete(Q_QUERY)
            prev.delete(Q_MODE)
          }
          return prev
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { isWheelchairModeLocked } = useWheelchairModeConfig()
  const [isWheelchairModeEnabled, setIsWheelchairModeEnabled] = useState(
    isWheelchairModeLocked
  )

  const [themeDirection, setThemeDirection] = useState(
    getDirectionFromLanguage(i18n.language)
  )

  const { mapLoading, mapLoaded: mapReady } = useIndoorMap()

  const { goBack } = useNavigationHistory()
  const {
    mainMenus,
    sideMenus,
    matchedPath,
    matchedRouteOption,
    routeOptions,
    updateRouteOptions,
  } = useAppNavigation()

  const openSearch = useCallback(() => {
    setOpenSearchState(true)
  }, [setOpenSearchState])

  const closeSearch = useCallback(() => {
    setOpenSearchState(false)
  }, [setOpenSearchState])

  const toggleSearch = useCallback(() => {
    setOpenSearchState((prev) => !prev)
  }, [setOpenSearchState])

  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const setWheelchairModeEnabled = useCallback(
    (isEnabled) => {
      if (isWheelchairModeLocked) return
      setIsWheelchairModeEnabled(isEnabled)
    },
    [isWheelchairModeLocked]
  )

  useEffect(() => {
    const newDirection = getDirectionFromLanguage(i18n.language)
    document.documentElement.setAttribute("dir", newDirection)
    setThemeDirection(newDirection)
  }, [i18n.language])

  // Disable right click on the screen when application is in kiosk mode
  useEffect(() => {
    if (config("env") !== "production" || !isKioskMode) return
    const handler = (event) => event.preventDefault()
    window.addEventListener("contextmenu", handler)
    return () => window.removeEventListener("contextmenu", handler)
  }, [isKioskMode])

  useEffect(() => {
    initVenueController()
  }, [initVenueController])

  useEffect(() => {
    if (isWheelchairModeLocked) {
      setIsWheelchairModeEnabled(true)
    }
  }, [isWheelchairModeLocked])

  useEffect(() => {
    isWheelchairModeEnabled
      ? addBodyWheelchairClass()
      : removeBodyWheelchairClass()
  }, [isWheelchairModeEnabled])

  const value = {
    mapLayout,
    setMapLayout,
    openSearchState,
    sidebarState,
    mapReady,
    mapLoading,
    isKioskMode,
    isWheelchairModeEnabled,
    isIdleTimerPaused,
    themeDirection,
    // Navigation
    mainMenus,
    sideMenus,
    matchedPath,
    matchedRouteOption,
    routeOptions,
    // functions
    openSearch,
    closeSearch,
    toggleSearch,
    setOpenSearchState,
    setSidebarState,
    goBack,
    setWheelchairModeEnabled,
    setIdleTimerPaused,
    updateRouteOptions,
  }

  return <AppUIContext.Provider value={value}>{children}</AppUIContext.Provider>
}
